import { useState, type CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { deploymentModels, deploymentOptions } from '../data';
import type { ModelId } from '../types';
import ArchitectureDiagram from './architecture-diagram';
import ComponentDetails from './component-details';
import ModelTabs from './model-tabs';
import RolloutSection from './rollout-section';
import ComparisonSection from './comparison-section';

const DeploymentExplorer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hash = location.hash.slice(1);
  const model: ModelId = hash === 'hybrid' || hash === 'onprem' ? hash : 'cloud';
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [showMigration, setShowMigration] = useState(false);
  const current = deploymentModels[model];
  const option = deploymentOptions.find(option => option.id === model)!;
  const activeNode = selectedNode && current.nodes[selectedNode] ? selectedNode : null;

  const selectModel = (id: ModelId) => {
    if (selectedNode && !deploymentModels[id].nodes[selectedNode]) setSelectedNode(null);
    navigate({ pathname: location.pathname, search: location.search, hash: id }, { replace: true });
  };

  return (
    <div style={{ '--deployment-accent': option.accent, '--deployment-tint': option.tint } as CSSProperties}>
      <ModelTabs model={model} onSelect={selectModel} />
      <div id="deployment-content" role="tabpanel" aria-labelledby={`deployment-tab-${model}`}>
        <div className="grid gap-[18px] min-[901px]:grid-cols-[minmax(0,1fr)_330px]">
          <Card className="relative block min-w-0 rounded-[18px] border-border p-3.5 pb-2 shadow-none">
            <div className="flex flex-wrap items-center justify-between gap-3 px-1.5 pt-1 pb-2.5">
              <p className="text-[15px] text-muted-foreground">{current.summary}</p>
              <label className="flex cursor-pointer items-center gap-2.5 text-[15px] select-none">
                <input
                  type="checkbox"
                  checked={showMigration}
                  onChange={event => setShowMigration(event.target.checked)}
                  className="relative m-0 h-[22px] w-10 shrink-0 cursor-pointer appearance-none rounded-full bg-border transition-colors checked:bg-(--deployment-accent) after:absolute after:top-[3px] after:left-[3px] after:size-4 after:rounded-full after:bg-white after:transition-transform checked:after:translate-x-[18px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--deployment-accent) motion-reduce:transition-none motion-reduce:after:transition-none"
                />
                Show how existing data is linked
              </label>
            </div>
            <ArchitectureDiagram model={model} showMigration={showMigration} activeNode={activeNode} onSelect={setSelectedNode} />
            <p className="px-2 pt-1.5 pb-1 text-[13.5px] text-muted-foreground">
              Moving dots show how studies and reports travel. Dashed lines show existing archive data being linked.
            </p>
          </Card>
          <ComponentDetails model={model} activeNode={activeNode} />
        </div>
        <RolloutSection model={model} />
        <ComparisonSection model={model} />
      </div>
    </div>
  );
};

export default DeploymentExplorer;
